<!-- HelpPopup.vue — the standard "?" in a circle that explains something in a popup (the content
     is the slot), 2026-10-14: /2026/Registration's regional rates (Registration/RatesTierHelp.vue),
     #profile's privacy settings (Profile/SettingsPrivacy.vue). #register has its own, the same look
     (fullstack Register/RatesHelp.vue). Opens on hover, keyboard focus or tap; stays open while
     the pointer or focus is inside it (so its links can be clicked); Escape closes it (not the
     dialog it's in). The bubble is shifted to stay inside its bounds: the window, or in a dialog
     the card (boundsSelector). isInDialog: #register's look, a brand-green outlined circle, on a
     dialog's light card; otherwise a filled circle in the page's text colour. mark "exclamation"
     (a "!") and isWarning (orange): a warning (none at present). -->

<template>
  <span
    class="helpPopup"
    :class="{isInDialog, isWarning}"
    @mouseenter="open"
    @mouseleave="closeSoon"
    @focusin="open"
    @focusout="closeSoon"
    @keydown.esc.stop="close"
  >
    <button
      ref="buttonRef"
      type="button"
      class="helpButton"
      :aria-label="label"
      :aria-expanded="isOpen"
      :aria-controls="id"
      @click="open"
    >
      <svg class="mark" viewBox="2 2 20 20" aria-hidden="true">
        <path v-if="mark === 'exclamation'" d="M12 7v5" />
        <path v-else d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </svg>
    </button>
    <span
      v-if="isOpen"
      :id
      ref="bubbleRef"
      class="bubble"
      :class="{isBelow}"
      role="tooltip"
      :style="{'--shift': `${shift}px`, maxWidth}"
    >
      <slot />
    </span>
  </span>
</template>

<script setup>
import {nextTick, onBeforeUnmount, ref} from "vue";

const props = defineProps({
  id: {type: String, required: true},
  label: {type: String, required: true}, // the button's accessible name, e.g. "About regional rates"
  isInDialog: {type: Boolean, default: false},
  boundsSelector: {type: String, default: ""}, // e.g. ".content": the dialog card
  mark: {type: String, default: "question"}, // or "exclamation": a warning or notice
  isWarning: {type: Boolean, default: false} // the circle in our attention orange
});

const CLOSE_DELAY_MS = 200; // time to move the pointer across the gap into the bubble
const MARGIN = 16; // the gutter the bubble keeps from its bounds' edges

function getBounds(button) {
  const container = props.boundsSelector && button?.closest(props.boundsSelector);
  if (container) return container.getBoundingClientRect();
  return {left: 0, right: window.innerWidth};
}

// the top a bubble above its "?" mustn't cross: the fixed banner's foot (--cta-height, set by
// CallToAction/Banner.vue), or in a dialog the card's top (bounds)
function getTopLimit(bounds) {
  if (bounds.top !== undefined) return bounds.top + MARGIN;
  const banner = getComputedStyle(document.documentElement).getPropertyValue("--cta-height");
  return (parseFloat(banner) || 0) + MARGIN;
}

// horizontal shift that brings the centred bubble back inside its bounds
function getShift(rectangle, bounds) {
  const overflowLeft = bounds.left + MARGIN - rectangle.left;
  if (overflowLeft > 0) return overflowLeft;
  const overflowRight = rectangle.right - (bounds.right - MARGIN);
  return overflowRight > 0 ? -overflowRight : 0;
}

const buttonRef = ref(null);
const bubbleRef = ref(null);
const isOpen = ref(false);
const shift = ref(0);
const isBelow = ref(false); // no room above (under the banner, or the card's top): opened below
const maxWidth = ref(""); // the bounds' width less the gutters: never wider than they are
let closeTimeoutId = null;

async function open() {
  clearTimeout(closeTimeoutId);
  if (isOpen.value) return;
  [shift.value, isBelow.value] = [0, false];
  const bounds = getBounds(buttonRef.value);
  maxWidth.value = `${bounds.right - bounds.left - 2 * MARGIN}px`;
  isOpen.value = true;
  await nextTick();
  if (!bubbleRef.value) return;
  const rectangle = bubbleRef.value.getBoundingClientRect();
  shift.value = getShift(rectangle, bounds);
  isBelow.value = rectangle.top < getTopLimit(bounds);
}

function closeSoon() {
  clearTimeout(closeTimeoutId);
  closeTimeoutId = setTimeout(() => (isOpen.value = false), CLOSE_DELAY_MS);
}

function close() {
  clearTimeout(closeTimeoutId);
  isOpen.value = false;
}

onBeforeUnmount(() => clearTimeout(closeTimeoutId));
</script>

<style scoped>
.helpPopup {
  --help-color: var(--text-color);
  position: relative;
  display: inline-block;
  margin-left: 0.35em; /* the word space before the "?" */
  vertical-align: 0.05em;
  text-transform: none; /* not the capitals of a heading it sits in */
  letter-spacing: normal;
}

/* "?" centred in a circle; resets global.css's button style (shadow, corners, padding) */
.helpButton {
  display: inline-grid;
  place-items: center;
  width: 1.25em;
  height: 1.25em;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--help-color);
  box-shadow: none;
  color: var(--background-color);
  font: inherit;
  font-size: 0.9em;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

/* ButtonBespoke's and the test-site banner's attention orange */
.helpPopup.isWarning {
  --help-color: var(--orange-on-light);
}

/* in a dialog, #register's look: an outlined brand-green circle, filled when open or hovered */
.isInDialog {
  --help-color: var(--brand-hover-color);
}
.isInDialog .helpButton {
  border: 1.5px solid var(--help-color);
  background: transparent;
  color: var(--help-color);
}
.isInDialog .helpButton[aria-expanded="true"],
.isInDialog .helpButton:hover {
  background: var(--help-color);
  color: #fff;
}

/* Optical centring: the "?" glyph's ink sits high and slightly left of its line box */
/* the "?" drawn, not typed: centred in the circle whatever the browser's font metrics (a typed
   one sat low in Firefox, 2026-10-15) */
.helpButton > .mark {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.helpButton:focus-visible {
  outline: 2px solid var(--help-color);
  outline-offset: 2px;
}

/* Dark bubble above the "?", centred on it unless that would leave its bounds. In dark mode a
   lighter fill and a faint light edge, so it stands off the page's near-black */
.bubble {
  --bubble-fill: #333;
  position: absolute;
  bottom: calc(100% + 0.6rem);
  left: 50%;
  z-index: 10;
  width: 19rem; /* 17rem of text and the padding; maxWidth keeps it inside its bounds */
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: var(--bubble-fill);
  color: #fff;
  font-weight: 400;
  transform: translateX(calc(-50% + var(--shift, 0px)));
}

/* the padding within the width, so the bubble fits its bounds (maxWidth) */
.bubble {
  box-sizing: border-box;
}
/* in px within a dialog, as the Profile dialog's own text: in rem, a browser with a smaller
   default font size shrank the bubble's text below the dialog's (Firefox 115, 2026-10-15);
   the sizes are those of 17rem, 0.75rem 1rem and 0.8rem at the default 16px */
.isInDialog .bubble {
  width: 272px; /* the padding within it, for a narrow card */
  padding: 12px 16px;
}
.isInDialog .bubble :deep(.helpParagraph) {
  font-size: 12.8px;
}

/* arrow: stays under the "?" when the bubble is shifted */
.bubble::after {
  content: "";
  position: absolute;
  top: 100%;
  left: calc(50% - var(--shift, 0px));
  transform: translateX(-50%);
  border-left: 0.5rem solid transparent;
  border-right: 0.5rem solid transparent;
  border-top: 0.55rem solid var(--bubble-fill);
}

/* no room above: below the "?", its arrow on top (Seán, 2026-11-03: under the banner on /People) */
.bubble.isBelow {
  top: calc(100% + 0.6rem);
  bottom: auto;
}
.bubble.isBelow::after {
  top: auto;
  bottom: 100%;
  border-top: none;
  border-bottom: 0.55rem solid var(--bubble-fill);
}

.darkMode .bubble {
  --bubble-fill: #4a4c50;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.22), 0 6px 18px rgba(0, 0, 0, 0.6);
}

/* the paragraphs and links of the slot */
.bubble :deep(.helpParagraph) {
  display: block;
  margin: 0 0 0.5em;
  font-size: 0.8rem;
  text-align: left;
  line-height: 1.4;
}
.bubble :deep(.helpParagraph:last-child) {
  margin-bottom: 0;
}
.bubble :deep(.helpParagraph a) {
  color: #fff;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15em;
}
</style>
